import { Button, Spinner, Stack, Tooltip, Typography } from "@altimateai/lego";
import { IconRefresh } from "@tabler/icons-react";
import { ChatbotProps, ChatError, ChatSession, sessionStatusSchema } from "../../types";
import { useEffect } from "react";
import { resolveStudioError } from "../../utils/studioErrorMessages";

const RefetchSession = ({
  error,
  currentSession,
  refetchSession,
  onRetry,
  onDismissNotice,
}: {
  error: ChatError | null;
  currentSession: ChatSession | null;
  refetchSession?: ChatbotProps["refetchSession"];
  onRetry?: () => void;
  onDismissNotice?: () => void;
}) => {
  useEffect(() => {
    if (
      (!error && currentSession?.status !== sessionStatusSchema.enum.pending) ||
      !refetchSession
    ) {
      return;
    }
    let intervalId: NodeJS.Timeout;
    const startRefetching = () => {
      intervalId = setInterval(() => {
        refetchSession();
      }, 30000); // refetch every 30 seconds
    };

    if (currentSession?.status === sessionStatusSchema.enum.pending) {
      startRefetching();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [error, refetchSession, currentSession?.status]);

  if (error?.notice) {
    return (
      <Stack
        role="status"
        direction="row"
        justify="between"
        align="center"
        className="al-p-4 al-rounded al-bg-muted al-mt-4"
      >
        <Typography size="sm">{error.message}</Typography>
        {onDismissNotice && (
          <Button variant="ghost" size="sm" onClick={onDismissNotice}>
            Dismiss
          </Button>
        )}
      </Stack>
    );
  }

  if (currentSession?.status === sessionStatusSchema.enum.pending) {
    return (
      <Stack align="center" role="alert">
        <Spinner />
        Please wait until we process your request...
      </Stack>
    );
  }

  if (error) {
    const { title, description } = resolveStudioError({
      code: error.code,
      fallbackMessage: error.message,
    });
    const showRetry = error.retryable === true && !!onRetry;

    return (
      <Stack
        direction="column"
        gap="sm"
        className="al-text-red-500 al-p-4 al-rounded al-bg-red-50 al-mt-4"
        role="alert"
      >
        <Typography variant="subheading">{title}</Typography>
        <Typography variant="body" size="sm">
          {description}
        </Typography>
        {showRetry && (
          <Stack direction="row">
            <Tooltip title="Retry sending your last message">
              <Button variant="outline" size="sm" onClick={onRetry}>
                <IconRefresh aria-hidden="true" />
                Retry
              </Button>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    );
  }

  return null;
};

export default RefetchSession;
